import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { select, Store } from '@ngrx/store';
import { AppState, RealEstateObject, ResponsiveTarget } from '@immosky/types';
import { Subscription } from 'rxjs';
import { clamp } from '@immosky/shared';
import {
  selectSimilarResults,
  selectSimilarResultsCount,
} from '../../state/results/similar-results/similar-results.selectors';
import {
  selectIsMapVisible,
  selectResponsiveTarget,
} from '@immosky/object-search-layout';
import { HammerGestureConfig } from '@angular/platform-browser';
import { DIRECTION_HORIZONTAL } from 'hammerjs';

class SimilarObjectsSliderHammerConfig extends HammerGestureConfig {
  buildHammer(element: HTMLElement) {
    return new Hammer(element, {
      touchAction: 'pan-y',
      cssProps: {
        contentZooming: '',
        tapHighlightColor: '',
        touchSelect: '',
        userDrag: '',
        userSelect: '',
        touchCallout: '',
      },
      recognizers: [
        [
          Hammer.Swipe,
          {
            direction: DIRECTION_HORIZONTAL,
            threshold: 0,
          },
        ],
      ],
    });
  }
}

@Component({
  selector: 'app-similar-objects-slider',
  templateUrl: './similar-objects-slider.component.html',
  styleUrls: ['./similar-objects-slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SimilarObjectsSliderComponent implements AfterViewInit, OnDestroy {
  private subscriptions: Subscription[] = [];

  objects: RealEstateObject[] = [];
  currentIndex = 0;

  isScrolling = false;
  isPanning = false;
  isMapVisible = false;

  hammerInstance!: HammerManager;

  similarObjectsCount$ = this.store.pipe(select(selectSimilarResultsCount));

  responsiveTarget: ResponsiveTarget = ResponsiveTarget.XS;

  @Input()
  isExpanded = false;

  @ViewChild('slidesContainer')
  slidesContainer!: ElementRef<HTMLElement>;

  @ViewChild('slide')
  firstSlide!: ElementRef<HTMLElement>;

  get transform(): string {
    if (this.isExpanded) {
      return 'none';
    }

    if (this.responsiveTarget < ResponsiveTarget.MD) {
      return `translateX(${272 * this.currentIndex * -1}px)`;
    } else {
      if (this.isMapVisible) {
        if (window.innerWidth >= 2300) {
          return `translateX(calc((33.33% - 10px) * ${
            this.currentIndex * -1
          }))`;
        } else {
          return `translateX(calc((50% - 14px) * ${this.currentIndex * -1}))`;
        }
      } else {
        return `translateX(calc((25% - 7px) * ${this.currentIndex * -1})`;
      }
    }

    return 'none';
  }

  constructor(
    private cdRef: ChangeDetectorRef,
    private store: Store<AppState>
  ) {
    const responsiveTargetSubscription = this.store
      .pipe(select(selectResponsiveTarget))
      .subscribe((responsiveTarget: ResponsiveTarget) => {
        this.responsiveTarget = responsiveTarget;
        this.cdRef.markForCheck();
      });

    const isMapVisibleSubscription = this.store
      .pipe(select(selectIsMapVisible))
      .subscribe((isMapVisible: boolean) => {
        this.isMapVisible = isMapVisible;
        this.cdRef.markForCheck();
      });

    const objectsSubscription = this.store
      .pipe(select(selectSimilarResults))
      .subscribe((objects: RealEstateObject[]) => {
        this.objects = objects;
        this.cdRef.markForCheck();
      });

    this.subscriptions.push(
      objectsSubscription,
      responsiveTargetSubscription,
      isMapVisibleSubscription
    );
  }

  ngAfterViewInit(): void {
    this.hammerInstance = new SimilarObjectsSliderHammerConfig().buildHammer(
      this.slidesContainer.nativeElement
    );
    this.hammerInstance.on('swipeleft', this.next.bind(this));
    this.hammerInstance.on('swiperight', this.previous.bind(this));
  }

  ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }

  previous() {
    this.goToIndex(this.currentIndex - 1);
  }

  next() {
    this.goToIndex(this.currentIndex + 1);
  }

  goToIndex(index: number) {
    this.currentIndex = clamp(0, index, this.objects.length);
    this.cdRef.detectChanges();
  }

  trackBy(index: number, object: RealEstateObject) {
    return object.id;
  }

  shouldHideSlide(index: number): boolean {
    if (this.isExpanded) {
      return false;
    }

    const shouldHideOnLeftSide = index < this.currentIndex;

    if (this.responsiveTarget < ResponsiveTarget.MD) {
      return shouldHideOnLeftSide;
    } else {
      if (this.isMapVisible) {
        if (window.innerWidth >= 2300) {
          return shouldHideOnLeftSide || index > this.currentIndex + 2;
        } else {
          return shouldHideOnLeftSide || index > this.currentIndex + 1;
        }
      } else {
        return shouldHideOnLeftSide || index > this.currentIndex + 3;
      }
    }

    return false;
  }
}
